export const INDUSTRY_OPTIONS = [
    "Education",
    "Real Estate",
    "Healthcare",
    "Ecommerce / Store",
    "Legal",
    "SaaS / Tech",
    "Restaurant",
    "Fitness",
    "Beauty",
    "Construction"
];

export const INDUSTRY_DEFAULTS = {
    Education: {
        subIndustry: "Sports Academy",
        primaryEntityType: "programs",
        businessModel: "academy",
        primaryWebsiteMode: "program-first",
        locationFocus: "local",
        pages: ["Home", "Programs", "Coaches", "Admissions", "Contact"],
        ctas: ["Book Trial", "View Programs", "Apply for Admission", "Talk to Coach"],
        audiences: ["Parents / students", "Kids 6-16", "Beginners", "Advanced learners"],
        offerings: ["Football training", "Weekend batches", "Beginner programs", "Advanced coaching", "Admissions enquiries"],
        description:
            "{businessName} helps students and parents discover structured training programs, expert coaches, admissions guidance, and clear enquiry options through a modern academy website."
    },
    "Real Estate": {
        subIndustry: "Real Estate Company",
        primaryEntityType: "listings",
        businessModel: "service",
        primaryWebsiteMode: "listing-first",
        locationFocus: "local",
        pages: ["Home", "Listings", "Services", "About", "Contact"],
        ctas: ["Schedule Site Visit", "View Listings", "Talk to Advisor", "Request Callback"],
        audiences: ["Home buyers", "Property investors", "Sellers", "Tenants"],
        offerings: ["Property listings", "Buying advisory", "Selling support", "Rental assistance", "Site visits"],
        description:
            "{businessName} helps property buyers, sellers, and investors explore listings, understand local market options, and connect with the team for site visits and property consultation."
    },
    Healthcare: {
        subIndustry: "Clinic",
        primaryEntityType: "doctors",
        businessModel: "service",
        primaryWebsiteMode: "appointment-first",
        locationFocus: "local",
        pages: ["Home", "Services", "Doctors", "Appointments", "Contact"],
        ctas: ["Book Appointment", "Call Clinic", "Meet Doctors", "Request Consultation"],
        audiences: ["Patients", "Families", "Local customers", "Health seekers"],
        offerings: ["Doctor consultations", "Treatments", "Appointments", "Patient support", "Health checkups"],
        description:
            "{businessName} provides patient-friendly healthcare services with clear treatment information, doctor profiles, appointment options, and trust-building sections for local patients."
    },
    "Ecommerce / Store": {
        subIndustry: "Online Store",
        primaryEntityType: "products",
        businessModel: "ecommerce",
        primaryWebsiteMode: "product-first",
        locationFocus: "national",
        pages: ["Home", "Shop", "Collections", "Offers", "Contact"],
        ctas: ["Shop Now", "View Collections", "Explore Offers", "Contact Store"],
        audiences: ["Online shoppers", "Fashion buyers", "Deal seekers", "Premium customers"],
        offerings: ["Products", "Collections", "Offers", "Online shopping", "Customer support"],
        description:
            "{businessName} offers a modern shopping experience with curated products, collections, offers, trust sections, and easy customer enquiry options."
    },
    Legal: {
        subIndustry: "Law Firm",
        primaryEntityType: "services",
        businessModel: "service",
        primaryWebsiteMode: "consultation-first",
        locationFocus: "local",
        pages: ["Home", "Practice Areas", "Team", "Consultation", "Contact"],
        ctas: ["Book Consultation", "View Practice Areas", "Talk to Legal Expert", "Request Case Review"],
        audiences: ["Business owners", "Individuals", "Companies", "Legal clients"],
        offerings: ["Consultations", "Practice areas", "Legal advisory", "Case evaluation", "Team profile"],
        description:
            "{businessName} provides professional legal guidance with clear practice areas, lawyer profiles, consultation options, and credibility-focused website sections."
    },
    "SaaS / Tech": {
        subIndustry: "SaaS Product",
        primaryEntityType: "features",
        businessModel: "saas",
        primaryWebsiteMode: "conversion-first",
        locationFocus: "global",
        pages: ["Home", "Features", "Pricing", "Use Cases", "Contact"],
        ctas: ["Start Free Trial", "Book Demo", "View Features", "Contact Sales"],
        audiences: ["Startups / teams", "Business owners", "Product teams", "Growing companies"],
        offerings: ["Product features", "Pricing", "Free trial", "Onboarding", "Team productivity"],
        description:
            "{businessName} helps teams understand the product, explore features, compare plans, and take action through a clean SaaS website experience."
    },
    Restaurant: {
        subIndustry: "Restaurant / Cafe",
        primaryEntityType: "services",
        businessModel: "service",
        primaryWebsiteMode: "local-conversion",
        locationFocus: "local",
        pages: ["Home", "Menu", "Gallery", "Offers", "Contact"],
        ctas: ["View Menu", "Order Now", "Book Table", "Call Restaurant"],
        audiences: ["Local customers", "Food lovers", "Families", "Office groups"],
        offerings: ["Menu", "Online orders", "Table booking", "Offers", "Location details"],
        description:
            "{businessName} is a local food destination with menu highlights, offers, gallery sections, location details, and quick actions for orders or table bookings."
    },
    Fitness: {
        subIndustry: "Fitness Studio",
        primaryEntityType: "programs",
        businessModel: "service",
        primaryWebsiteMode: "program-first",
        locationFocus: "local",
        pages: ["Home", "Programs", "Trainers", "Memberships", "Contact"],
        ctas: ["Book Trial", "View Memberships", "Talk to Trainer", "Join Program"],
        audiences: ["Fitness seekers", "Local customers", "Beginners", "Personal training clients"],
        offerings: ["Training programs", "Membership plans", "Personal coaching", "Class schedule", "Trial session"],
        description:
            "{businessName} helps members improve strength, stamina, and lifestyle through guided training programs, modern equipment, personal coaching, and flexible membership options."
    },
    Beauty: {
        subIndustry: "Salon / Beauty Studio",
        primaryEntityType: "services",
        businessModel: "service",
        primaryWebsiteMode: "appointment-first",
        locationFocus: "local",
        pages: ["Home", "Services", "Packages", "Gallery", "Contact"],
        ctas: ["Book Appointment", "View Services", "Explore Packages", "Call Studio"],
        audiences: ["Local customers", "Premium clients", "Beauty seekers", "Brides"],
        offerings: ["Beauty services", "Appointments", "Packages", "Before-after gallery", "Customer reviews"],
        description:
            "{businessName} offers beauty and grooming services with service menus, packages, appointment options, gallery sections, and customer trust signals."
    },
    Construction: {
        subIndustry: "Construction Company",
        primaryEntityType: "services",
        businessModel: "service",
        primaryWebsiteMode: "portfolio-first",
        locationFocus: "local",
        pages: ["Home", "Projects", "Services", "About", "Contact"],
        ctas: ["Request Quote", "View Projects", "Book Consultation", "Contact Team"],
        audiences: ["Property owners", "Developers", "Business owners", "Home renovators"],
        offerings: ["Project portfolio", "Services", "Consultation", "Site enquiry", "Client trust"],
        description:
            "{businessName} helps clients plan and execute construction or renovation projects with clear services, project portfolio, trust signals, and consultation options."
    }
};

export function getIndustryDefaults(industry) {
    return INDUSTRY_DEFAULTS[industry] || {
        subIndustry: industry || "Business",
        primaryEntityType: "services",
        businessModel: "service",
        primaryWebsiteMode: "conversion-first",
        locationFocus: "local",
        pages: ["Home", "About", "Services", "Contact"],
        ctas: ["Get Quote", "Contact Us", "Request Callback", "Book Consultation"],
        audiences: ["Local customers", "Business owners", "Premium clients"],
        offerings: ["Services", "Consultation", "Lead capture", "Customer support"],
        description:
            "{businessName} is a professional business focused on helping customers understand services, build trust, and take action through a clear website experience."
    };
}

export function buildIndustryDescription(industry, businessName) {
    const defaults = getIndustryDefaults(industry);
    const name = businessName?.trim() || "This business";

    return defaults.description.replace("{businessName}", name);
}

export function inferIndustryFromPrompt(prompt) {
    const value = String(prompt || "").toLowerCase();
    const hasAny = (words) => words.some((word) => value.includes(word));

    if (hasAny([
        "gym",
        "fitness",
        "trainer",
        "workout",
        "bodybuilding",
        "strength",
        "cardio",
        "equipment",
        "machines",
        "personal training",
        "crossfit",
        "yoga",
        "pilates"
    ])) {
        return {
            industry: "Fitness",
            ...getIndustryDefaults("Fitness"),
            goalOfWebsite: "Book Trial",
            primaryAudience: "Fitness seekers"
        };
    }

    if (hasAny(["academy", "school", "coaching", "sports", "football", "cricket", "training students"])) {
        return {
            industry: "Education",
            ...getIndustryDefaults("Education"),
            goalOfWebsite: "Generate Leads",
            primaryAudience: "Parents / students"
        };
    }

    if (hasAny(["real estate", "property", "listing", "flat", "apartment", "villa", "broker", "realtor"])) {
        return {
            industry: "Real Estate",
            ...getIndustryDefaults("Real Estate"),
            goalOfWebsite: "Generate Leads",
            primaryAudience: "Home buyers"
        };
    }

    if (hasAny(["clinic", "doctor", "health", "hospital", "patient", "medical", "dental"])) {
        return {
            industry: "Healthcare",
            ...getIndustryDefaults("Healthcare"),
            goalOfWebsite: "Book Appointments",
            primaryAudience: "Patients"
        };
    }

    if (hasAny(["store", "shop", "ecommerce", "product", "fashion", "clothes", "boutique"])) {
        return {
            industry: "Ecommerce / Store",
            ...getIndustryDefaults("Ecommerce / Store"),
            goalOfWebsite: "Sell Products",
            primaryAudience: "Online shoppers"
        };
    }

    if (hasAny(["law", "legal", "firm", "advocate", "lawyer", "attorney"])) {
        return {
            industry: "Legal",
            ...getIndustryDefaults("Legal"),
            goalOfWebsite: "Book Consultation",
            primaryAudience: "Business owners"
        };
    }

    if (hasAny(["restaurant", "cafe", "food", "menu", "dining", "pizza", "bakery"])) {
        return {
            industry: "Restaurant",
            ...getIndustryDefaults("Restaurant"),
            goalOfWebsite: "Get Orders",
            primaryAudience: "Local customers"
        };
    }

    if (hasAny(["salon", "beauty", "spa", "makeup", "hair", "skincare"])) {
        return {
            industry: "Beauty",
            ...getIndustryDefaults("Beauty"),
            goalOfWebsite: "Book Appointments",
            primaryAudience: "Local customers"
        };
    }

    if (hasAny(["construction", "builder", "contractor", "interior", "architect", "renovation"])) {
        return {
            industry: "Construction",
            ...getIndustryDefaults("Construction"),
            goalOfWebsite: "Generate Leads",
            primaryAudience: "Property owners"
        };
    }

    if (hasAny(["saas", "software", "web app", "mobile app", "platform", "dashboard", "subscription product"])) {
        return {
            industry: "SaaS / Tech",
            ...getIndustryDefaults("SaaS / Tech"),
            goalOfWebsite: "Start Free Trial",
            primaryAudience: "Startups / teams"
        };
    }

    return {
        industry: "",
        subIndustry: "",
        primaryEntityType: "",
        businessModel: "",
        primaryWebsiteMode: "",
        locationFocus: "",
        pages: ["Home", "About", "Services", "Contact"],
        ctas: ["Get Quote", "Contact Us", "Request Callback"],
        audiences: ["Local customers"],
        offerings: ["Services"],
        goalOfWebsite: "Generate Leads",
        primaryAudience: "Local customers"
    };
}